import { BoxItem } from '@/box/interface'
import { getMqttDrive, getPlcDrive } from '@/drive/service'
import { AppModuleCheckList } from '@/shared/AppModuleCheckList'
import {
  defaultMqttCombinations,
  defaultMqttTimestamps,
  MqttCombination,
  MqttTimestamp,
  variableBatchLimit
} from '@/shared/DefaultValues'
import { Guid } from '@/utils/guid'
import { getFormatMessage } from '@/utils/utils'
import { LeftOutlined } from '@ant-design/icons'
import { Drawer, message, Modal, Space } from 'antd'
import React, { useState } from 'react'
import { MacroItem } from '..'
import { TemplateApply } from './apply'
import { TemplateEditModal } from './edit/modal'
import { TemplateImportModal } from './import/modal'
import {
  AlarmItem,
  PlcItem,
  TemplateItem,
  TemplateProps,
  TemplateReferenceItem,
  TransferFormatItem,
  TransferItem,
  VariableItem
} from './interface'
import { TemplateTab } from './tab'
import { TemplateTable } from './table'
import { TemplateToolbar } from './toolbar'
import moment from 'moment'
import { createFile } from '@/utils/file'

export const Template: React.FC<TemplateProps> = (props: TemplateProps) => {
  const [editVisible, setEditVisible] = useState<boolean>(false)
  const [configVisible, setConfigVisible] = useState<boolean>(false)
  const [applyVisible, setApplyVisible] = useState<boolean>(false)
  const [importVisible, setImportVisible] = useState<boolean>(false)
  const [template, setTemplate] = useState<TemplateItem>({})
  const [plcs, setPlcs] = useState<PlcItem[]>([])
  const [variables, setVariables] = useState<VariableItem[]>([])
  const [alarms, setAlarms] = useState<AlarmItem[]>([])
  const [macros, setMacros] = useState<MacroItem[]>([])
  const [transfers, setTransfers] = useState<TransferItem[]>([])
  const [combinations, setCombinations] = useState<MqttCombination[]>([])
  const [timestamps, setTimestamps] = useState<MqttTimestamp[]>([])
  const [selectBoxes, setSelectBoxes] = useState<BoxItem[]>([])
  const [applyRecordVisible, setApplyRecordVisible] = useState<boolean>(false)
  const [edit, setEdit] = useState<boolean>(false)
  const [appModuleVisible, setAppModuleVisible] = useState<boolean>(false)
  const [selected, setSelected] = useState<TemplateItem[]>([])
  const formatMessage = getFormatMessage()
  const titleStyle = {
    fontSize: 12
  }

  /**
   * 删除监控点关联MQTT变量
   * @param variableIds 监控点ID
   */
  const deleteMqttVariables = (
    variableIds: (string | undefined)[]
  ): TransferItem[] | undefined => {
    return template.transfers?.map((x) => {
      for (const func of x.function_config || []) {
        const recursion = (formatItems: TransferFormatItem[]) => {
          for (let i = formatItems.length; i--; ) {
            if (variableIds.includes(formatItems[i].item_id || '')) {
              formatItems.splice(i, 1)
            }
          }
          for (const format of formatItems) {
            if (format.children && format.children.length > 0) {
              recursion(format.children)
            }
          }
        }
        recursion(func.pub?.format || [])
        recursion(func.sub?.format || [])
      }
      return x
    })
  }

  /**
   * 判断监控点是否关联MQTT变量
   * @param variableIds 监控点ID
   */
  const existMqttVariables = (variableIds: (string | undefined)[]): boolean => {
    for (const x of template.transfers || []) {
      for (const func of x.function_config || []) {
        const recursion = (formatItems: TransferFormatItem[]): boolean => {
          for (const format of formatItems) {
            if (variableIds.includes(format.item_id || '')) {
              return true
            }

            if (format.children && format.children.length > 0) {
              if (recursion(format.children)) return true
            }
          }

          return false
        }
        if (recursion(func.pub?.format || [])) return true
        if (recursion(func.sub?.format || [])) return true
      }
    }

    return false
  }

  /**
   * 如果变量存在关联数据
   */
  const existAssociatedData = (variableIds: (string | undefined)[]) => {
    // 报警
    const alarmIds = template.alarms
      ?.filter((x) => variableIds.includes(x.variableId))
      .map((x) => x.id)

    if (alarmIds && alarmIds.length > 0) {
      return true
    }

    // 宏
    const macroVarIds = template.macros
      ?.flatMap((x) => x.variableIds)
      .filter((x) => variableIds.includes(x))

    if (macroVarIds && macroVarIds.length > 0) {
      return true
    }

    // 宏执行条件
    const macroConditions = template.macros
      ?.flatMap((x) => x.schedules)
      .filter((x) => variableIds.includes(x?.condition?.variableId))

    if (macroConditions && macroConditions.length > 0) {
      return true
    }

    // mqtt
    const mqttIds = variableIds.concat(alarmIds)
    return existMqttVariables(mqttIds)
  }

  /**
   * 删除变量
   * @param variableIds
   */
  const deleteVariables = (variableIds: (string | undefined)[]) => {
    // 待移除的报警Id
    const alarmIds = template.alarms
      ?.filter((x) => variableIds.includes(x.variableId))
      .map((x) => x.id)

    // 待移除MQTT变量
    const mqttIds = variableIds.concat(alarmIds)

    // 移除变量
    template.variables = template.variables?.filter(
      (x) => !variableIds.includes(x.id)
    )

    // 移除报警
    template.alarms = template.alarms?.filter((x) => !alarmIds?.includes(x.id))

    // 移除MQTT变量
    template.transfers = deleteMqttVariables(mqttIds)

    // 移除宏脚本的关联变量
    template.macros?.forEach((macro) => {
      // 关联变量
      macro.variableIds = macro.variableIds?.filter(
        (x) => !variableIds.includes(x)
      )

      // 执行条件
      macro.schedules = macro.schedules?.filter(
        (x) => !variableIds.includes(x.condition?.variableId)
      )
    })
  }

  /**
   * 判断组合方式是否被惯量MQTT变量
   * @param id 组合方式ID
   */
  const existCombination = (id: string): boolean => {
    for (const x of template.transfers || []) {
      for (const func of x.function_config || []) {
        const recursion = (formatItems: TransferFormatItem[]): boolean => {
          for (const format of formatItems) {
            if (format.combe_type === id) {
              return true
            }

            if (format.children && format.children.length > 0) {
              if (recursion(format.children)) return true
            }
          }

          return false
        }
        if (recursion(func.pub?.format || [])) return true
        if (recursion(func.sub?.format || [])) return true
      }
    }

    return false
  }

  /**
   * 判断时间格式是否被使用
   * @param item 时间格式对象(取format字段判断)
   */
  const existTimestamp = (item: MqttTimestamp): boolean => {
    for (const x of template.transfers || []) {
      if (x.timestamp_format?.time_format === item.format) return true
    }

    return false
  }

  /**
   * 模板迁移
   * 此方法用于不同版本的模板格式迁移
   */
  const migrateTemplate = async (item: TemplateItem): Promise<void> => {
    // 迁移北向驱动下载地址
    if (item.transfers && item.transfers.length > 0) {
      for (let transfer of item.transfers) {
        if (!transfer.drive) continue
        // 更新驱动下载地址
        const mqttDrive = await getMqttDrive(0, 0)
        transfer.drive.checkUrl = mqttDrive.checkUrl
        transfer.drive.soFileMd5 = mqttDrive.md5
        transfer.drive.soFileUrl = mqttDrive.downloadUrl
      }
    }

    // 迁移PLC驱动下载地址
    if (item.plcs && item.plcs.length > 0) {
      for (let plc of item.plcs) {
        if (!plc.drive) continue
        const plcDrive = await getPlcDrive(plc.plcId!)
        plc.drive.soFileMd5 = plcDrive.soFileMd5
        plc.drive.soFileUrl = plcDrive.soFileUrl
      }
    }

    // 迁移北向时间格式和组合方式
    if (item.transfers && item.transfers.length > 0) {
      for (const transfer of item.transfers) {
        let timestamp = defaultMqttTimestamps[0]
        if (transfer.timestamp_format) {
          timestamp =
            item.timestamps?.find(
              (x) => x.format === transfer.timestamp_format?.time_format
            ) || defaultMqttTimestamps[0]
        } else {
          transfer.timestamp_format = {}
        }
        // 重新赋值
        transfer.timestamp_format.time_format = timestamp.format
        transfer.timestamp_format.time_zone = timestamp.zone
      }
    }

    // 新增后端接口API(针对域名白名单)
    // const exposedApiUrl = moduleConfig.exposedApiUrl

    // 替换Url
    // const replaceUrl = (url: string): string => {
    //   try {
    //     const source = new URL(url)
    //     const path = source.pathname.substring(0, 6)
    //     if (path === '/proxy') {
    //       return url.replace(source.origin, `${exposedApiUrl}`)
    //     } else {
    //       return url.replace(source.origin, `${exposedApiUrl}/proxy`)
    //     }
    //   } catch {
    //     // url 地址可能不符合规则
    //     return url
    //   }
    // }

    /** MQTT驱动支持接口检查，需要将已有模板迁移 */
    // if (item.transfers && item.transfers.length > 0) {
    //   item.transfers.forEach((x) => {
    //     if (x.drive) {
    //       // checkUrl 处理
    //       if (x.drive.checkUrl) {
    //         // 如果CheckUrl已经存在则检查CheckUrl和API是否一致，不一致则迁移
    //         if (exposedApiUrl && !x.drive.checkUrl.includes(exposedApiUrl)) {
    //           x.drive.checkUrl = replaceUrl(x.drive.checkUrl)
    //         }
    //       } else {
    //         // 迁移模板使用 checkUrl 代替 soFileUrl，但保留soFileUrl兼容低版本固件
    //         x.drive.checkUrl = exposedApiUrl
    //           ? getMqttDriveCheckUrl(`${exposedApiUrl}/proxy`)
    //           : getMqttDriveCheckUrl()
    //       }

    //       // 兼容已过时低版本固件
    //       if (x.drive.soFileUrl) {
    //         if (exposedApiUrl && !x.drive.soFileUrl.includes(exposedApiUrl)) {
    //           x.drive.soFileUrl = replaceUrl(x.drive.soFileUrl)
    //         }
    //       }
    //     }
    //   })
    // }

    /** 迁移模板下载地址，如果存在exposedApiUrl，将所有plc驱动下载地址迁移为exposedApiUrl */
    // if (exposedApiUrl) {
    //   item.plcs?.forEach((x) => {
    //     if (x.drive && x.drive.soFileUrl) {
    //       if (!x.drive.soFileUrl.includes(exposedApiUrl)) {
    //         x.drive.soFileUrl = replaceUrl(x.drive.soFileUrl)
    //       }
    //     }
    //   })
    // }
  }

  const findMqttVariableIds = (ids: string[]) => {
    const findIds: string[] = []
    for (const x of template.transfers || []) {
      for (const func of x.function_config || []) {
        const recursion = (formatItems: TransferFormatItem[]): void => {
          for (const format of formatItems) {
            if (ids.includes(format.item_id!)) {
              findIds.push(format.item_id!)
            }
            if (format.children && format.children.length > 0) {
              recursion(format.children)
            }
          }
        }
        recursion(func.pub?.format || [])
        recursion(func.sub?.format || [])
      }
    }
    return findIds
  }

  /**
   * 查找模板条目引用
   */
  const findTemplateReference = (
    idType: 'plc' | 'variable' | 'alarm' | 'macro' | 'transfer',
    ids: string[],
    templateItem: TemplateItem
  ): TemplateReferenceItem[] => {
    const refs: TemplateReferenceItem[] = []

    switch (idType) {
      // 查找PLC相关的引用
      case 'plc':
        // 查找是否被变量引用
        const variableIds = templateItem.variables
          ?.filter((x) => ids.includes(x.deviceId!))
          .map((x) => x.id!)
        if (variableIds && variableIds.length > 0) {
          refs.push({ objectType: 1, objectIds: variableIds })
        }
        break

      // 查找变量相关引用
      case 'variable':
        // 查找是否被报警引用
        const alarmIds = templateItem.alarms
          ?.filter((x) => ids.includes(x.variableId!))
          .map((x) => x.id!)

        if (alarmIds && alarmIds.length > 0) {
          refs.push({ objectType: 2, objectIds: alarmIds })
        }

        // 查找是否被宏脚本引用
        const macroVarIds = templateItem.macros
          ?.flatMap((x) => x.variableIds!)
          .filter((x) => ids.includes(x))

        if (macroVarIds && macroVarIds.length > 0) {
          refs.push({ objectType: 3, objectIds: macroVarIds })
        }

        // 脚本执行计划
        const planVarIds = templateItem.macros
          ?.flatMap((x) => x.schedules)
          .filter((x) => x?.mode === 2)
          .map((x) => x?.condition?.variableId!)
          .filter((x) => ids.includes(x))

        if (planVarIds && planVarIds.length > 0) {
          refs.push({ objectType: 5, objectIds: planVarIds })
        }

        // 查找是否被MQTT引用
        const mqttVarIds = findMqttVariableIds(ids)
        if (mqttVarIds && mqttVarIds.length > 0) {
          refs.push({ objectType: 4, objectIds: mqttVarIds })
        }
        break

      // 查找报警相关引用
      case 'alarm':
        // 查找是否被MQTT引用
        const mqttAlarmVarIds = findMqttVariableIds(ids)
        if (mqttAlarmVarIds && mqttAlarmVarIds.length > 0) {
          refs.push({ objectType: 4, objectIds: mqttAlarmVarIds })
        }
        break
    }
    return refs
  }

  const releaseRef = (
    idType: 'plc' | 'variable' | 'alarm' | 'macro' | 'transfer',
    ids: string[],
    ref: TemplateReferenceItem
  ) => {
    // 移除变量引用
    if (ref.objectType === 1) {
      const variableIds = ref.objectIds || []
      deleteVariables(variableIds)
    }

    // 移除报警引用
    if (ref.objectType === 2) {
      const alarmIds = ref.objectIds || []
      template.alarms = template.alarms?.filter(
        (x) => !alarmIds.includes(x.id!)
      )
      template.transfers = deleteMqttVariables(alarmIds)
    }

    // 移除宏引用
    if (ref.objectType === 3) {
      const macroVarIds = ref.objectIds || []
      template.macros?.forEach((macro) => {
        macro.variableIds = macro.variableIds?.filter(
          (x) => !macroVarIds.includes(x)
        )
      })
    }

    // 移除转发引用
    if (ref.objectType === 4) {
      deleteMqttVariables(ids)
    }

    // 移除脚本中的执行条件
    if (ref.objectType === 5) {
      const macroPlanVarIds = ref.objectIds || []
      template.macros?.forEach((macro) => {
        macro.schedules = macro.schedules?.filter(
          (x) => !macroPlanVarIds.includes(x.condition?.variableId!)
        )
      })
    }

    const tmp = { ...template }
    updateTemplate(tmp)
    return findTemplateReference(idType, ids, tmp)
  }

  const resetTemplate = (item: TemplateItem) => {
    setTemplate(item)
    setPlcs(item.plcs || [])
    setVariables(item.variables || [])
    setAlarms(item.alarms || [])
    setMacros(item.macros || [])
    setTransfers(item.transfers || [])
    setCombinations(
      item.combinations && item.combinations.length > 0
        ? item.combinations
        : defaultMqttCombinations
    )
    setTimestamps(
      item.timestamps && item.timestamps.length > 0
        ? item.timestamps
        : defaultMqttTimestamps
    )
  }

  const handleConfig = (item: TemplateItem) => {
    setConfigVisible(true)
    resetTemplate(item)
  }

  const handlePlcCreate = (item: PlcItem) => {
    const plcs = [...(template.plcs || [])]
    const i = plcs.findIndex((x) => x.id == item.id)
    if (i >= 0) {
      plcs[i] = item
    } else {
      plcs.push(item)
    }
    template.plcs = plcs
    updateTemplate({ ...template })
  }

  const handlePlcDelete = (items: PlcItem[]): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
      // plc Ids
      const ids = items.map((x) => x.id)

      // 待移除的变量ID
      const variableIds =
        template.variables
          ?.filter((x) => ids.includes(x.deviceId))
          .map((x) => x.id) || []

      if (variableIds.length > 0) {
        Modal.confirm({
          title: '确认删除此设备',
          content:
            '此设备已创建变量，删除设备将同步删除变量及相关转发，删除后不可恢复，是否继续删除？',
          okText: formatMessage({ id: 'app.modal.ok' }),
          cancelText: formatMessage({ id: 'app.modal.cancel' }),
          onOk() {
            // 移除PLC
            template.plcs = template.plcs?.filter((x) => !ids.includes(x.id))
            // 移除变量
            deleteVariables(variableIds)
            updateTemplate({ ...template })
            resolve(true)
          },
          onCancel() {
            resolve(false)
          }
        })
      } else {
        Modal.confirm({
          title: '确认删除此设备',
          content: '删除后不可恢复，是否继续删除？',
          okText: formatMessage({ id: 'app.modal.ok' }),
          cancelText: formatMessage({ id: 'app.modal.cancel' }),
          onOk() {
            // 移除PLC
            template.plcs = template.plcs?.filter((x) => !ids.includes(x.id))
            updateTemplate({ ...template })
            resolve(true)
          },
          onCancel() {
            resolve(false)
          }
        })
      }
    })
  }

  const handlePlcSearch = (args: {
    alias?: string
    plcName?: string
    type?: string
  }) => {
    let items = template.plcs || []
    if (args.alias) {
      items = items?.filter((x) => x.alias?.includes(args.alias || ''))
    }
    if (args.plcName) {
      items = items?.filter((x) => x.alias?.includes(args.plcName || ''))
    }
    if (args.type) {
      items = items?.filter((x) => x.type == args.type)
    }
    setPlcs(items)
  }

  const handlePlcRefCheck = (items: PlcItem[]): TemplateReferenceItem[] => {
    const ids = items.map((x) => x.id!) || []
    return findTemplateReference('plc', ids, template)
  }

  const handlePlcRefRelease = (
    items: PlcItem[],
    ref: TemplateReferenceItem
  ): TemplateReferenceItem[] => {
    const ids = items.map((x) => x.id!)
    return releaseRef('plc', ids, ref)
  }

  const getVariableName = (key: string, items: VariableItem[]) => {
    let index = 0
    while (true) {
      const i = items.findIndex((x) => x.name === `${key}${index}`)
      if (i < 0) break
      index++
    }
    return `${key}${index}`
  }

  const handleVariableCreate = (item: VariableItem) => {
    const variables = [...(template.variables || [])]
    // 批量添加
    if (item['batch']) {
      const batch = item['batch'] as any
      if (
        batch.count > variableBatchLimit[1] ||
        batch.count < variableBatchLimit[0]
      ) {
        message.error(
          formatMessage({
            id: 'app.template.variable.batchLimit',
            parameters: {
              min: variableBatchLimit[0],
              max: variableBatchLimit[1]
            }
          })
        )
        return
      }
      for (let i = 0; i < batch.count; i++) {
        const newItem = { ...item }
        // 更新Id
        newItem.id = Guid.raw()
        if (batch.type == 0) {
          newItem.mainAddress = item.mainAddress! + batch.increment * i
          // newItem.name = `${item.name}${newItem.mainAddress}`
          newItem.name = getVariableName(item.name!, variables)
          newItem.description = `${item.registerName}${newItem.mainAddress}`
        } else {
          newItem.subAddress = item.subAddress! + batch.increment * i
          // newItem.name = `${item.name}${newItem.subAddress}`
          newItem.name = getVariableName(item.name!, variables)
          newItem.description = `${item.registerName}${newItem.subAddress}`
        }
        delete newItem['batch']
        variables.push(newItem)
      }
    } else {
      const i = variables.findIndex((x) => x.id == item.id)
      if (i >= 0) {
        variables[i] = item
      } else {
        variables.push(item)
      }
    }
    template.variables = variables
    updateTemplate({ ...template })
  }

  const handleVariableDelete = (items: VariableItem[]): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
      // 待移除的变量ID
      const variableIds = items.map((x) => x.id)
      if (existAssociatedData(variableIds)) {
        Modal.confirm({
          title: '确认删除此变量',
          content:
            '此变量已存在关联数据，将自动删除对应关联数据，删除后不可恢复，是否继续删除？',
          okText: formatMessage({ id: 'app.modal.ok' }),
          cancelText: formatMessage({ id: 'app.modal.cancel' }),
          onOk() {
            deleteVariables(variableIds)
            updateTemplate({ ...template })
            resolve(true)
          },
          onCancel() {
            resolve(false)
          }
        })
      } else {
        Modal.confirm({
          title: '确认删除此变量',
          content: '删除后不可恢复，是否继续删除？',
          okText: formatMessage({ id: 'app.modal.ok' }),
          cancelText: formatMessage({ id: 'app.modal.cancel' }),
          onOk() {
            // 移除变量
            template.variables = template.variables?.filter(
              (x) => !variableIds.includes(x.id)
            )
            updateTemplate({ ...template })
            resolve(true)
          },
          onCancel() {
            resolve(false)
          }
        })
      }
    })
  }

  const handleVariableSearch = (args: {
    name?: string
    description?: string
  }) => {
    let items = template.variables || []
    if (args.name) {
      items = items?.filter((x) => x.name?.includes(args.name || ''))
    }
    if (args.description) {
      items = items?.filter((x) =>
        x.description?.includes(args.description || '')
      )
    }
    setVariables(items)
  }

  const handleVariableRefCheck = (
    items: VariableItem[]
  ): TemplateReferenceItem[] => {
    const ids = items.map((x) => x.id!) || []
    return findTemplateReference('variable', ids, template)
  }

  const handleVariableRefRelease = (
    items: VariableItem[],
    ref: TemplateReferenceItem
  ): TemplateReferenceItem[] => {
    const ids = items.map((x) => x.id!)
    return releaseRef('variable', ids, ref)
  }

  const hanldeAlarmCreate = (item: AlarmItem) => {
    const alarms = [...(template.alarms || [])]
    const i = alarms.findIndex((x) => x.id == item.id)
    if (i >= 0) {
      alarms[i] = item
    } else {
      alarms.push(item)
    }
    template.alarms = alarms
    updateTemplate({ ...template })
  }

  const hanldeAlarmDelete = (items: AlarmItem[]): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
      const alarmIds = items.map((x) => x.id)
      if (existMqttVariables(alarmIds)) {
        Modal.confirm({
          title: '确认删除此报警',
          content:
            '此报警已经存在关联数据，将自动删除对应关联数据，删除后不可恢复，是否继续删除？',
          okText: formatMessage({ id: 'app.modal.ok' }),
          cancelText: formatMessage({ id: 'app.modal.cancel' }),
          onOk() {
            // 移除报警
            template.alarms = template.alarms?.filter(
              (x) => !alarmIds.includes(x.id)
            )
            template.transfers = deleteMqttVariables(alarmIds)
            updateTemplate({ ...template })
            resolve(true)
          },
          onCancel() {
            resolve(false)
          }
        })
      } else {
        Modal.confirm({
          title: '确认删除此报警',
          content: '删除后不可恢复，是否继续删除？',
          okText: formatMessage({ id: 'app.modal.ok' }),
          cancelText: formatMessage({ id: 'app.modal.cancel' }),
          onOk() {
            // 移除报警
            template.alarms = template.alarms?.filter(
              (x) => !alarmIds.includes(x.id)
            )
            updateTemplate({ ...template })
            resolve(true)
          },
          onCancel() {
            resolve(false)
          }
        })
      }
    })
  }

  const handleAlarmSearch = (args: { name?: string }) => {
    let items = template.alarms || []
    if (args.name) {
      items = items?.filter((x) => x.name?.includes(args.name || ''))
    }

    setAlarms(items)
  }

  const handleAlarmRefCheck = (items: AlarmItem[]): TemplateReferenceItem[] => {
    const ids = items.map((x) => x.id!) || []
    return findTemplateReference('alarm', ids, template)
  }

  const handleAlarmRefRelease = (
    items: AlarmItem[],
    ref: TemplateReferenceItem
  ): TemplateReferenceItem[] => {
    const ids = items.map((x) => x.id!)
    return releaseRef('alarm', ids, ref)
  }

  const handleMacroCreate = (item: MacroItem) => {
    const macros = [...(template.macros || [])]
    const i = macros.findIndex((x) => x.id == item.id)
    if (i >= 0) {
      macros[i] = item
    } else {
      macros.push(item)
    }

    template.macros = macros
    updateTemplate({ ...template })
  }

  const handleMacroDelete = (items: MacroItem[]): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
      const macroIds = items.map((x) => x.id)
      Modal.confirm({
        title: '确认删除此脚本',
        content: '删除后不可恢复，是否继续删除？',
        okText: formatMessage({ id: 'app.modal.ok' }),
        cancelText: formatMessage({ id: 'app.modal.cancel' }),
        onOk() {
          // 移除宏脚本
          template.macros = template.macros?.filter(
            (x) => !macroIds.includes(x.id)
          )
          updateTemplate({ ...template })
          resolve(true)
        },
        onCancel() {
          resolve(false)
        }
      })
    })
  }

  const handleMacroSearch = (args: { name?: string }) => {
    let items = template.macros || []
    if (args.name) {
      items = items?.filter((x) => x.name?.includes(args.name || ''))
    }
    setMacros(items)
  }

  const handleTransferCreate = (item: TransferItem) => {
    const transfers = [...(template.transfers || [])]
    const i = transfers.findIndex((x) => x.id == item.id)
    if (i >= 0) {
      transfers[i] = item
    } else {
      transfers.push(item)
    }
    template.transfers = transfers
    if (!template.combinations || template.combinations.length <= 0) {
      template.combinations = combinations
    }

    if (!template.timestamps || template.timestamps.length <= 0) {
      template.timestamps = timestamps
    }
    updateTemplate({ ...template })
  }

  const handleTransferDelete = (item: TransferItem) => {
    template.transfers = template.transfers?.filter((x) => x.id != item.id)
    updateTemplate({ ...template })
  }

  const handleTransferChange = (item: TransferItem) => {
    const transfers = [...(template.transfers || [])]
    if (item.enable === true) {
      for (let x of transfers) {
        if (x.id == item.id) {
          x.enable = true
        } else {
          x.enable = false
        }
      }
    }
    template.transfers = transfers
    updateTemplate({ ...template })
  }

  const hanldeCombinationCreate = (item: MqttCombination) => {
    const combinations = [...(template.combinations || [])]
    const i = combinations.findIndex((x) => x.id == item.id)
    if (i >= 0) {
      combinations[i] = item
    } else {
      combinations.push(item)
    }
    template.combinations = combinations
    updateTemplate({ ...template })
  }

  const hanldeCombinationDelete = (item: MqttCombination) => {
    if (existCombination(item.id || '')) {
      Modal.error({
        title: '系统提示',
        content: '该组合方式已经绑定MQTT变量无法删除。',
        okText: formatMessage({ id: 'app.modal.ok' }),
        cancelText: formatMessage({ id: 'app.modal.cancel' })
      })
      return
    }
    template.combinations = template.combinations?.filter(
      (x) => x.id != item.id
    )
    updateTemplate({ ...template })
  }

  const handleTimestampCreate = (item: MqttTimestamp) => {
    const timestamps = [...(template.timestamps || [])]
    const i = timestamps.findIndex((x) => x.id == item.id)
    if (i >= 0) {
      timestamps[i] = item
    } else {
      timestamps.push(item)
    }
    template.timestamps = timestamps
    updateTemplate({ ...template })
  }

  const handleTimestampDelete = (item: MqttTimestamp) => {
    if (existTimestamp(item)) {
      Modal.error({
        title: '系统提示',
        content: '该时间格式已经被MQTT连接使用，无法删除。',
        okText: formatMessage({ id: 'app.modal.ok' }),
        cancelText: formatMessage({ id: 'app.modal.cancel' })
      })
      return
    }
    template.timestamps = template.timestamps?.filter((x) => x.id != item.id)
    updateTemplate({ ...template })
  }

  const updateTemplate = (template: TemplateItem) => {
    migrateTemplate(template)
    resetTemplate(template)
    if (props.onTemplateUpdate) {
      props.onTemplateUpdate(template)
    }
  }

  const handleApply = (item: TemplateItem) => {
    setApplyVisible(true)
    resetTemplate(item)
  }

  const handleApplyBox = () => {
    // 选择性下发
    setAppModuleVisible(true)
    // 全量下发
    // setApplyVisible(false)
    // if (props.onTemplateApply) {
    //   props.onTemplateApply(template, selectBoxes, defaultAppModules)
    // }
  }

  const handleAppModuleConfirm = (modules: string[]) => {
    setAppModuleVisible(false)
    setApplyVisible(false)
    if (props.onTemplateApply && modules.length > 0) {
      props.onTemplateApply(template, selectBoxes, modules)
    }
  }

  const handleImportTemplate = async (items: TemplateItem[]) => {
    if (props.onTemplateImport) {
      // 导入时执行数据迁移
      for (const item of items) {
        await migrateTemplate(item)
        props.onTemplateImport(item)
      }
    }
  }

  const handleViewRecord = (item: TemplateItem) => {
    setApplyRecordVisible(true)
  }

  const handleCopy = (item: TemplateItem) => {
    const newItem = { ...item }
    newItem.id = Guid.raw()
    newItem.applied = false
    newItem.name = `${newItem.name}-${formatMessage({
      id: 'app.template.edit.name.copy'
    })}`
    resetTemplate(newItem)
    setEditVisible(true)
  }

  const handleEdit = (item: TemplateItem) => {
    setEdit(true)
    resetTemplate(item)
    setEditVisible(true)
  }

  const handleTemplateCreate = () => {
    const item: TemplateItem = {
      plcs: [],
      variables: [],
      transfers: [],
      applied: false
    }
    resetTemplate(item)
    setEditVisible(true)
  }

  const handleFormSubmit = (item: TemplateItem) => {
    if (edit) {
      if (props.onTemplateUpdate) {
        props.onTemplateUpdate(item)
        setEdit(false)
      }
    } else {
      if (props.onTemplateCreate) {
        props.onTemplateCreate(item)
      }
    }
  }

  const handleTemplateDelete = (item: TemplateItem) => {
    if (props.onTemplateDelete) {
      props.onTemplateDelete([item])
    }
  }

  const handleMenuItemClick = (key: string) => {
    if (!selected) {
      message.error(formatMessage({ id: 'app.template.prompt.select' }))
      return
    }

    switch (key) {
      case 'delete':
        const names = selected.map((x) => `【${x.name}】`).join('、')
        Modal.confirm({
          title: formatMessage({ id: 'app.modal.title' }),
          content: formatMessage({
            id: 'app.template.prompt.delete',
            parameters: { names: names }
          }),
          okText: formatMessage({ id: 'app.modal.ok' }),
          cancelText: formatMessage({ id: 'app.modal.cancel' }),
          onOk: async () => {
            if (props.onTemplateDelete) {
              props.onTemplateDelete(selected)
              setSelected([])
            }
          }
        })
        break
      case 'export':
        exportTemplates(selected)
        break
    }
  }

  const exportTemplates = async (items: TemplateItem[]) => {
    for (const [index, item] of items.entries()) {
      let newItem = await props.onItemLoad?.(item.id!)
      if (newItem) {
        const json = JSON.stringify(newItem, undefined, 2)
        const time = moment().format('YYYYMMDDHHmmss')
        createFile(`se_template_${time}_${index}.json`, json)
      }
    }
  }

  return (
    <div>
      <TemplateToolbar
        selectable={selected.length > 0}
        searchItem={props.searchItem}
        onImport={() => setImportVisible(true)}
        onCreate={handleTemplateCreate}
        onSearch={props.onSearch}
        onMenuItemClick={handleMenuItemClick}
      />
      <TemplateTable
        data={props.data}
        selectable={true}
        pagination={props.pagination}
        onConfig={handleConfig}
        onItemLoad={props.onItemLoad}
        onApply={handleApply}
        onDelete={handleTemplateDelete}
        onViewRecord={handleViewRecord}
        onCopy={handleCopy}
        onEdit={handleEdit}
        onSelect={setSelected}
      />
      <TemplateEditModal
        visible={editVisible}
        onVisibleChange={setEditVisible}
        editItem={template}
        onSave={handleFormSubmit}
      />
      <Modal
        title={`${formatMessage({ id: 'app.template.tab.title' })}(${
          template.name
        })`}
        visible={configVisible}
        onCancel={() => setConfigVisible(false)}
        onOk={() => setConfigVisible(false)}
        okText={formatMessage({ id: 'app.modal.ok' })}
        cancelText={formatMessage({ id: 'app.modal.cancel' })}
        width={1000}
        maskClosable={false}
        destroyOnClose={true}
        footer={null}
      >
        <TemplateTab
          {...props}
          plcs={plcs}
          variables={variables}
          alarms={alarms}
          macros={macros}
          transfers={transfers}
          combinations={combinations}
          timestamps={timestamps}
          onPlcCreate={handlePlcCreate}
          onPlcDelete={handlePlcDelete}
          onPlcSearch={handlePlcSearch}
          onPlcRefCheck={handlePlcRefCheck}
          onPlcRefRelease={handlePlcRefRelease}
          onVariableCreate={handleVariableCreate}
          onVariableDelete={handleVariableDelete}
          onVariableSearch={handleVariableSearch}
          onVariableRefCheck={handleVariableRefCheck}
          onVariableRefRelease={handleVariableRefRelease}
          onTransferCreate={handleTransferCreate}
          onTransferDelete={handleTransferDelete}
          onTransferChange={handleTransferChange}
          onCombinationCreate={hanldeCombinationCreate}
          onCombinationDelete={hanldeCombinationDelete}
          onTimestampCreate={handleTimestampCreate}
          onTimestampDelete={handleTimestampDelete}
          onAlarmCreate={hanldeAlarmCreate}
          onAlarmDelete={hanldeAlarmDelete}
          onAlarmSearch={handleAlarmSearch}
          onAlarmRefCheck={handleAlarmRefCheck}
          onAlarmRefRelease={handleAlarmRefRelease}
          onMacroCreate={handleMacroCreate}
          onMacroDelete={handleMacroDelete}
          onMacroSearch={handleMacroSearch}
        />
      </Modal>
      <Modal
        title={formatMessage({ id: 'app.template.tab.title' })}
        visible={applyVisible}
        onCancel={() => setApplyVisible(false)}
        onOk={handleApplyBox}
        okText={formatMessage({ id: 'app.modal.ok' })}
        cancelText={formatMessage({ id: 'app.modal.cancel' })}
        width={800}
        maskClosable={false}
        destroyOnClose={true}
      >
        <TemplateApply
          data={props.boxes!}
          pagination={props.boxPagination}
          onSelect={(items) => setSelectBoxes(items)}
          onSearch={props.onBoxSearch}
        />
      </Modal>
      <TemplateImportModal
        visible={importVisible}
        onVisibleChange={setImportVisible}
        onImport={handleImportTemplate}
      />
      <Drawer
        destroyOnClose={true}
        maskClosable={false}
        title={
          <Space>
            <LeftOutlined />
            <span>{formatMessage({ id: 'app.template.apply.title' })}</span>
            <span style={titleStyle}></span>
            <span style={titleStyle}>|</span>
            <span style={titleStyle}></span>
          </Space>
        }
        visible={applyRecordVisible}
        onClose={() => setApplyRecordVisible(false)}
        width={800}
      />
      <AppModuleCheckList
        visible={appModuleVisible}
        template={template}
        boxItems={selectBoxes}
        onOk={handleAppModuleConfirm}
        onCancel={() => setAppModuleVisible(false)}
      />
    </div>
  )
}
